"use strict";
/**
 * PNL Page Handler
 *
 * Wires the bet-performance page. The Events tab picks venue + league (reusing
 * the events-page league selectors), lists the events the operator actually
 * traded, and drills into one for realized PNL plus the trade list. The Monthly
 * tab shows one card per month across venues (ui/pnl-monthly.ts). The owner can
 * view the whole page as another user (ui/pnl-view-as.ts).
 *
 * Read-only: this page never polls and holds no market state.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupPnlPage = setupPnlPage;
const novig_1 = require("../api/novig");
const constants_1 = require("../markets/constants");
const state_1 = require("../state");
const navigation_service_1 = require("../services/navigation-service");
const pnl_1 = require("../api/pnl");
const data_source_service_1 = require("../services/data-source-service");
const pill_selector_1 = require("../ui/pill-selector");
const pnl_display_1 = require("../ui/pnl-display");
const pnl_monthly_1 = require("../ui/pnl-monthly");
const pnl_view_as_1 = require("../ui/pnl-view-as");
/**
 * Remembered viewing preferences, not state: a bad or stale value falls back to
 * the default (the EV interval is also resolved against what the server offers),
 * so neither can wedge the view.
 */
const DRIFT_INTERVAL_KEY = "pnl.driftInterval";
const TAB_KEY = "pnl.tab";
/** Before any logging; the response's `dataFrom` says where data really starts. */
const MONTHLY_FROM = "2020-01-01T00:00:00.000Z";
function readStored(key) {
    try {
        return window.localStorage.getItem(key) ?? undefined;
    }
    catch {
        // Storage can throw (private mode, disabled cookies) — just use the default.
        return undefined;
    }
}
function store(key, value) {
    try {
        window.localStorage.setItem(key, value);
    }
    catch {
        // Non-fatal: the choice simply doesn't survive a reload.
    }
}
const readStoredInterval = () => (0, pnl_display_1.parseInterval)(readStored(DRIFT_INTERVAL_KEY)) ?? undefined;
const readStoredTab = () => readStored(TAB_KEY) === "monthly" ? "monthly" : "events";
/**
 * Novig's leagues as Novig spells them (the stored league). Not through
 * populateFlatLeagueSelector, which also rewrites app-wide league state.
 */
async function populateNovigLeagues(select) {
    select.innerHTML = "";
    try {
        for (const league of await (0, novig_1.listNovigLeagues)()) {
            const option = document.createElement("option");
            option.value = league;
            option.textContent = (0, constants_1.novigLeagueLabel)(league);
            select.appendChild(option);
        }
    }
    catch (error) {
        console.error("[pnl] Failed to load Novig leagues:", error);
    }
}
function setupPnlPage() {
    const backBtn = document.getElementById("pnl-back-btn");
    const venuePills = (0, pill_selector_1.initPillSelector)(document.getElementById("pnl-venue"));
    const leagueSel = document.getElementById("pnl-market-category");
    const listEl = document.getElementById("pnl-events-list");
    const loadingEl = document.getElementById("pnl-loading");
    const errorEl = document.getElementById("pnl-error");
    const detailEl = document.getElementById("pnl-detail");
    const tabPills = (0, pill_selector_1.initPillSelector)(document.getElementById("pnl-tabs"));
    const scopePills = (0, pill_selector_1.initPillSelector)(document.getElementById("pnl-scope"));
    const eventsControls = document.getElementById("pnl-events-controls");
    const monthlyEl = document.getElementById("pnl-monthly");
    const monthlyBody = document.getElementById("pnl-monthly-body");
    const scopeEl = document.getElementById("pnl-scope");
    const viewAsInput = document.getElementById("pnl-view-as-input");
    const viewAsResults = document.getElementById("pnl-view-as-results");
    const viewAsBanner = document.getElementById("pnl-view-as-banner");
    const viewAsName = document.getElementById("pnl-view-as-name");
    /**
     * Owner only: the user every read answers as. In memory and cleared on each
     * page entry, so you can never come back to the page still looking through
     * someone else's eyes without the banner having just been set.
     */
    let viewingAs = null;
    /** Loaded on the picker's first focus, then kept until the page is left. */
    let users = null;
    if (!venuePills || !leagueSel || !listEl || !detailEl)
        return;
    const getVenue = () => venuePills.value === "pm-us" || venuePills.value === "novig" ? venuePills.value : "kalshi";
    const setLoading = (on) => {
        loadingEl?.classList.toggle("hidden", !on);
    };
    const hideError = () => errorEl?.classList.add("hidden");
    const showError = (message) => {
        if (!errorEl)
            return;
        errorEl.textContent = message;
        errorEl.classList.remove("hidden");
    };
    // Accumulated (across "Load more") events for the current venue+league, keyed
    // by id so a repeat across a page boundary MERGES its marketIds instead of
    // being dropped (the detail view needs the full traded-market set). PM-US
    // pages a window at a time; Kalshi returns everything at once.
    let events = [];
    const eventsById = new Map();
    let cursor;
    let exhausted = true;
    let loading = false;
    /** Bumped by every reset; a response from an older one is discarded. */
    let eventsSeq = 0;
    // The open detail, kept so an EV-interval change re-renders its trade table
    // without a refetch (every interval's drift is already in the payload).
    let detail = null;
    let driftInterval = readStoredInterval();
    /** Return to the events list, clearing any open detail view. */
    const showList = () => {
        detailEl.classList.add("hidden");
        detailEl.innerHTML = "";
        detail = null;
        listEl.classList.remove("hidden");
    };
    /** Repopulate the league selector for the current venue. */
    const refreshLeagueOptions = async () => {
        if (getVenue() === "kalshi") {
            (0, data_source_service_1.populateKalshiLeagues)(leagueSel);
        }
        else if (getVenue() === "novig") {
            await populateNovigLeagues(leagueSel);
        }
        else {
            await (0, data_source_service_1.populateFlatLeagueSelector)(leagueSel, "pm-us");
        }
        // Prepend an "All leagues" default (no filter) and select it: the list
        // groups by game across every series, so it must not start on one series
        // (e.g. a Kalshi league value is the moneyline series only).
        const allOption = document.createElement("option");
        allOption.value = "";
        allOption.textContent = "All leagues";
        leagueSel.insertBefore(allOption, leagueSel.firstChild);
        leagueSel.value = "";
    };
    /**
     * Load a page of events. `reset` starts a fresh list (venue/league change or
     * page entry); otherwise it appends the next page ("Load more"), deduping by
     * eventId so an event split across a page boundary isn't shown twice.
     */
    const loadEventsList = async (reset) => {
        // Empty league = all leagues (the default). A reset — new venue, league or
        // viewer — always runs and supersedes anything in flight; "Load more"
        // waits its turn.
        const league = leagueSel.value;
        if (loading && !reset)
            return;
        if (reset) {
            eventsSeq++;
            events = [];
            eventsById.clear();
            cursor = undefined;
            exhausted = true;
            showList();
            listEl.innerHTML = "";
        }
        const seq = eventsSeq;
        loading = true;
        hideError();
        setLoading(true);
        try {
            const page = await (0, pnl_1.getPnlEvents)(getVenue(), league, reset ? undefined : cursor, viewingAs?.id);
            // Superseded: never render a list fetched for another venue or viewer.
            if (seq !== eventsSeq)
                return;
            for (const event of page.events) {
                const existing = eventsById.get(event.eventId);
                if (existing) {
                    // Same event on a later page — union the traded market ids.
                    existing.marketIds = [
                        ...new Set([...existing.marketIds, ...event.marketIds]),
                    ];
                    continue;
                }
                eventsById.set(event.eventId, event);
                events.push(event);
            }
            cursor = page.nextCursor;
            exhausted = page.exhausted;
            (0, pnl_display_1.renderPnlEventsList)(events, listEl, { hasMore: !exhausted });
        }
        catch (error) {
            if (seq !== eventsSeq)
                return;
            console.error("[pnl] Failed to load events:", error);
            showError("Failed to load PNL history.");
        }
        finally {
            if (seq === eventsSeq) {
                loading = false;
                setLoading(false);
            }
        }
    };
    const loadDetail = async (eventId) => {
        hideError();
        setLoading(true);
        const asUser = viewingAs?.id;
        try {
            const marketIds = eventsById.get(eventId)?.marketIds;
            const fetched = await (0, pnl_1.getPnlDetail)(getVenue(), eventId, marketIds, asUser);
            // The viewer changed while it loaded: this detail is someone else's.
            if (viewingAs?.id !== asUser)
                return;
            detail = fetched;
            // Settle on an interval the server actually offers, so a stored value
            // from an older sampling set doesn't stick around unselected.
            driftInterval = (0, pnl_display_1.resolveDriftInterval)(detail.driftIntervals ?? [], driftInterval);
            listEl.classList.add("hidden");
            detailEl.classList.remove("hidden");
            (0, pnl_display_1.renderPnlDetail)(detail, detailEl, { driftInterval, title: eventsById.get(eventId)?.title });
        }
        catch (error) {
            if (viewingAs?.id !== asUser)
                return;
            console.error("[pnl] Failed to load detail:", error);
            showError("Failed to load event PNL.");
        }
        finally {
            // A discarded detail mustn't clear the spinner of the new viewer's load.
            if (viewingAs?.id === asUser)
                setLoading(false);
        }
    };
    // --- Monthly tab ---------------------------------------------------------
    // Each load takes a number; only the newest may render, so a quick Me/All
    // flip can't be overwritten by the slower, older response.
    let monthlySeq = 0;
    let eventsLoaded = false;
    const loadMonthly = async () => {
        if (!monthlyBody)
            return;
        const seq = ++monthlySeq;
        hideError();
        setLoading(true);
        try {
            const result = await (0, pnl_1.getPnlTotals)({
                from: MONTHLY_FROM,
                to: new Date().toISOString(),
                bucket: "month",
                groupBy: ["venue"],
                scope: scopePills?.value === "all" ? "all" : "me",
                viewAs: viewingAs?.id,
            });
            if (seq !== monthlySeq)
                return;
            (0, pnl_monthly_1.renderPnlMonthly)((0, pnl_monthly_1.buildMonthlyModel)(result, new Date()), monthlyBody);
        }
        catch (error) {
            if (seq !== monthlySeq)
                return;
            console.error("[pnl] Failed to load monthly totals:", error);
            showError("Failed to load monthly PNL.");
        }
        finally {
            if (seq === monthlySeq)
                setLoading(false);
        }
    };
    /** Show one tab; the other's content stays in the DOM, hidden. */
    const selectTab = (tab) => {
        const monthly = tab === "monthly";
        hideError();
        eventsControls?.classList.toggle("hidden", monthly);
        monthlyEl?.classList.toggle("hidden", !monthly);
        if (monthly) {
            listEl.classList.add("hidden");
            detailEl.classList.add("hidden");
            loadMonthly();
            return;
        }
        showList();
        if (!eventsLoaded) {
            eventsLoaded = true;
            refreshLeagueOptions().then(() => loadEventsList(true));
        }
    };
    tabPills?.onChange((value) => {
        const tab = value === "monthly" ? "monthly" : "events";
        store(TAB_KEY, tab);
        selectTab(tab);
    });
    scopePills?.onChange(() => loadMonthly());
    // --- Owner "view as" ------------------------------------------------------
    const showResults = (open) => {
        viewAsResults?.classList.toggle("hidden", !open);
    };
    const refreshResults = () => {
        if (!viewAsResults || !users)
            return;
        (0, pnl_view_as_1.renderUserResults)((0, pnl_view_as_1.filterUsers)(users, viewAsInput?.value ?? ""), viewAsResults);
        showResults(true);
    };
    /** Reflect who the page answers as, then reload the visible tab as them. */
    const applyViewAs = (user) => {
        viewingAs = user;
        viewAsBanner?.classList.toggle("hidden", !user);
        if (viewAsName)
            viewAsName.textContent = user?.name ?? "";
        // The viewed user is a non-owner: they have no "All".
        scopeEl?.classList.toggle("hidden", !!user);
        if (tabPills?.value === "monthly") {
            // The events list belongs to the previous viewer; reload it when shown.
            eventsLoaded = false;
            loadMonthly();
        }
        else {
            eventsLoaded = true;
            loadEventsList(true);
        }
    };
    viewAsInput?.addEventListener("focus", async () => {
        if (!users) {
            try {
                users = await (0, pnl_1.getPnlUsers)();
            }
            catch (error) {
                console.error("[pnl] Failed to load users:", error);
                showError("Failed to load users.");
                return;
            }
        }
        refreshResults();
    });
    viewAsInput?.addEventListener("input", refreshResults);
    viewAsInput?.addEventListener("keydown", (e) => {
        if (e.key === "Escape")
            showResults(false);
    });
    viewAsResults?.addEventListener("click", (e) => {
        const option = e.target.closest(".pnl-view-as-option");
        const picked = users?.find((u) => u.id === option?.dataset.userId);
        if (!picked)
            return;
        showResults(false);
        if (viewAsInput) {
            viewAsInput.value = "";
            viewAsInput.blur();
        }
        applyViewAs(picked);
    });
    document.getElementById("pnl-view-as-exit")?.addEventListener("click", () => applyViewAs(null));
    // A tap anywhere else closes the list, as a native dropdown would.
    document.addEventListener("click", (e) => {
        const target = e.target;
        if (!document.getElementById("pnl-view-as")?.contains(target)) {
            showResults(false);
        }
    });
    venuePills.onChange(async () => {
        await refreshLeagueOptions();
        loadEventsList(true);
    });
    leagueSel.addEventListener("change", () => loadEventsList(true));
    backBtn?.addEventListener("click", () => (0, navigation_service_1.navigateToEvents)());
    // Delegated clicks: pick an event row, "Load more", or return from detail.
    listEl.addEventListener("click", (e) => {
        const target = e.target;
        if (target.closest("#pnl-load-more")) {
            loadEventsList(false);
            return;
        }
        const item = target.closest(".pnl-event-item");
        if (item?.dataset.eventId)
            loadDetail(item.dataset.eventId);
    });
    detailEl.addEventListener("click", (e) => {
        const target = e.target;
        if (target.id === "pnl-detail-back") {
            showList();
            return;
        }
        // EV interval chip: re-render only the trade table (the payload already
        // carries every interval), so the operator keeps their scroll position.
        const chip = target.closest(".pnl-ev-chip");
        if (chip && detail) {
            const picked = (0, pnl_display_1.parseInterval)(chip.dataset.interval);
            if (picked === null || picked === driftInterval)
                return;
            driftInterval = picked;
            store(DRIFT_INTERVAL_KEY, String(picked));
            (0, pnl_display_1.applyDriftInterval)(detailEl, detail.trades, picked);
        }
    });
    // Initialize once each time the page is entered (fresh selectors + list).
    let entered = false;
    (0, state_1.subscribe)((state) => {
        if (state.currentPage !== "pnl") {
            entered = false;
            return;
        }
        if (entered)
            return;
        entered = true;
        eventsLoaded = false;
        viewingAs = null;
        users = null;
        viewAsBanner?.classList.add("hidden");
        scopeEl?.classList.remove("hidden");
        if (viewAsInput)
            viewAsInput.value = "";
        showResults(false);
        // Without the tab markup there is only the Events view.
        const tab = tabPills ? readStoredTab() : "events";
        tabPills?.setValue(tab);
        selectTab(tab);
    });
}
